import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { LoginPage } from './LoginPage';
import { SignupPage } from './SignupPage';
import { ForgotPassword } from './ForgotPassword';

type AuthView = 'login' | 'signup' | 'forgot-password';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { user, isLoading, initialize } = useAuthStore();
  // Open directly on signup when arriving via the Worlds front-door handoff
  // (`#signup`), so a logged-out visitor who picked a world lands on the right
  // form. Otherwise default to login.
  const [view, setView] = useState<AuthView>(() =>
    typeof window !== 'undefined' && window.location.hash === '#signup' ? 'signup' : 'login'
  );

  // Initialize auth on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Loading state
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Not authenticated - show auth screens
  if (!user) {
    switch (view) {
      case 'signup':
        return (
          <SignupPage
            onSwitchToLogin={() => setView('login')}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPassword
            onSwitchToLogin={() => setView('login')}
          />
        );
      case 'login':
      default:
        return (
          <LoginPage
            onSwitchToSignup={() => setView('signup')}
            onSwitchToForgotPassword={() => setView('forgot-password')}
          />
        );
    }
  }

  // Authenticated - show the app
  return <>{children}</>;
}

function LoadingScreen() {
  return (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingContent}>
        <h1 style={styles.loadingTitle}>MACHINA</h1>
        <div style={styles.loadingSpinner}>
          <div style={styles.spinnerRing} />
        </div>
        <p style={styles.loadingText}>Preparing the realm...</p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-background)',
  },

  loadingContent: {
    textAlign: 'center',
  },

  loadingTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    letterSpacing: '0.3em',
    color: 'var(--color-accent)',
    margin: 0,
    marginBottom: 'var(--spacing-lg)',
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 'var(--spacing-md)',
  },

  spinnerRing: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--color-border)',
    borderTopColor: 'var(--color-accent)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  loadingText: {
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    margin: 0,
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
